/**
 * JWT Token Utilities
 * Handles token decoding and expiration checking
 */

/**
 * Decode a JWT payload without verification
 * @param {string} token - JWT token string
 * @returns {object|null} Decoded payload or null if invalid
 */
export function decodeToken(token) {
  if (!token) return null;
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error("Failed to decode token:", error);
    return null;
  }
}

/**
 * Check if a JWT token is expired
 * @param {string} token - JWT token string
 * @returns {boolean} true if token is expired or invalid
 */
export function isTokenExpired(token) {
  const payload = decodeToken(token);
  if (!payload || !payload.exp) return true;
  const currentTime = Date.now() / 1000;
  return payload.exp < currentTime;
}

/**
 * Get time remaining until token expiration
 * @param {string} token - JWT token string
 * @returns {number} Milliseconds until expiration, or 0 if expired/invalid
 */
export function getTimeUntilExpiration(token) {
  const payload = decodeToken(token);
  if (!payload || !payload.exp) return 0;
  const expirationTime = payload.exp * 1000;
  const remaining = expirationTime - Date.now();
  return Math.max(0, remaining);
}

/**
 * Get token expiration date as a Date object
 * @param {string} token - JWT token string
 * @returns {Date|null} Expiration date or null if invalid
 */
export function getTokenExpirationDate(token) {
  const payload = decodeToken(token);
  if (!payload || !payload.exp) return null;
  return new Date(payload.exp * 1000);
}
